import React, { useState, useEffect, useRef } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './MultiVariantNetwork.css';

// d3-force's forceCenter is needed to keep disconnected graphs from flying away
const { forceCollide, forceCenter } = require('d3-force');

const PALETTE = {
  primary: '#38bdf8',
  secondary: '#a855f7',
  accent: '#f59e0b',
  text: '#e5e7eb',
  variant: '#3b82f6',
  gene: '#8b5cf6',
  drug: '#f97316',
  protein: '#10b981'
};

const MultiVariantNetwork = ({ data }) => {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [scoreRange, setScoreRange] = useState([0.5, 1]);
  const [totalConnections, setTotalConnections] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [placeholder, setPlaceholder] = useState(null);
  const [visibleNodeTypes, setVisibleNodeTypes] = useState({
    variant: true,
    gene: true,
    drug: true,
    protein: true
  });
  
  // State for interaction types and their visibility
  const [interactionTypes, setInteractionTypes] = useState([]);
  const [visibleInteractionTypes, setVisibleInteractionTypes] = useState({});

  // State for the link hover tooltip
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });

  const [selectedNode, setSelectedNode] = useState(null);

  const containerRef = useRef();
  const fgRef = useRef();

  // Parse network data from TSV
  const parseNetworkData = (content) => {
    const lines = content.trim().split('\n');
    if (lines.length < 2) return { nodes: [], links: [] }; // Handle empty or header-only data
    
    const headers = lines[0].split('\t');
    const nodes = new Map();
    const links = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t');
      const row = {};
      headers.forEach((header, idx) => {
        row[header.trim()] = values[idx] ? values[idx].trim() : '';
      });
      
      const source = row.from || row.Node1 || row.source;
      const target = row.to || row.Node2 || row.target;
      const sourceType = (row['from.type'] || row.fromType || 'unknown').toLowerCase();
      const targetType = (row['to.type'] || row.toType || 'unknown').toLowerCase();
      const weight = parseFloat(row.Weight || row.weight || row.score || 0);
      const interactionType = row.Type || row.interaction_type || row.type || 'unknown';
      
      if (source && target && !isNaN(weight)) {
        if (!nodes.has(source)) {
          nodes.set(source, { id: source, name: source, type: sourceType });
        }
        if (!nodes.has(target)) {
          nodes.set(target, { id: target, name: target, type: targetType });
        }
        links.push({ source, target, weight, interactionType, id: `${source}-${target}` });
      }
    }
    return { nodes: Array.from(nodes.values()), links };
  };

  // Initialize graph data and discover interaction types
  useEffect(() => {
    if (!data) {
      setPlaceholder('No network data available');
      return;
    }
    
    try {
      const parsedData = parseNetworkData(data);
      setTotalConnections(parsedData.links.length);
      
      if (parsedData.nodes.length === 0) {
        setPlaceholder('No valid network connections found in the data.');
        return;
      }

      // Discover all unique interaction types from the data
      const allInteractionTypes = [...new Set(parsedData.links.map(l => l.interactionType))].sort();
      setInteractionTypes(allInteractionTypes);
      
      // Initialize visibility state for interaction types (all visible by default)
      const initialInteractionVisibility = allInteractionTypes.reduce((acc, type) => {
        acc[type] = true;
        return acc;
      }, {});
      setVisibleInteractionTypes(initialInteractionVisibility);
      
      setPlaceholder(null);
      // Apply initial filters with the newly discovered interaction types
      filterGraphData(parsedData, scoreRange, visibleNodeTypes, initialInteractionVisibility);
    } catch (error) {
      console.error('Error parsing network data:', error);
      setPlaceholder('Error parsing network data. Check console for details.');
    }
  }, [data]);

  // Filter graph data based on all criteria
  const filterGraphData = (sourceData, range, nodeTypes, interactionTypesVisibility) => {
    const [minScore, maxScore] = range;

    // 1. Filter nodes based on their type visibility first.
    const visibleNodes = sourceData.nodes.filter(node => nodeTypes[node.type]);
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));

    // 2. Filter links by interaction type, score range, AND ensure both source and target nodes are of a visible type.
    const finalLinks = sourceData.links.filter(link => 
      interactionTypesVisibility[link.interactionType] &&
      link.weight >= minScore && 
      link.weight <= maxScore &&
      visibleNodeIds.has(link.source) &&
      visibleNodeIds.has(link.target)
    );

    // 3. Create a set of node IDs that are actually part of the filtered links.
    const connectedNodeIds = new Set();
    finalLinks.forEach(link => {
      connectedNodeIds.add(link.source);
      connectedNodeIds.add(link.target);
    });

    // 4. Final nodes are the ones that are both of a visible type AND connected.
    const finalNodes = visibleNodes.filter(node => connectedNodeIds.has(node.id));
    
    setGraphData({ nodes: finalNodes, links: finalLinks });
  };


  // Handle score range change
  const handleScoreRangeChange = (newRange) => {
    setScoreRange(newRange);
    if (data) {
      const sourceData = parseNetworkData(data);
      filterGraphData(sourceData, newRange, visibleNodeTypes, visibleInteractionTypes);
    }
  };

  // Handle node type visibility change
  const handleNodeTypeChange = (nodeType) => {
    const newVisibleTypes = { ...visibleNodeTypes, [nodeType]: !visibleNodeTypes[nodeType] };
    setVisibleNodeTypes(newVisibleTypes);
    if (data) {
      const sourceData = parseNetworkData(data);
      filterGraphData(sourceData, scoreRange, newVisibleTypes, visibleInteractionTypes);
    }
  };

  // Handle interaction type visibility change
  const handleInteractionTypeChange = (interactionType) => {
    const newVisibleInteractionTypes = { 
      ...visibleInteractionTypes, 
      [interactionType]: !visibleInteractionTypes[interactionType] 
    };
    setVisibleInteractionTypes(newVisibleInteractionTypes);
    if (data) {
      const sourceData = parseNetworkData(data);
      filterGraphData(sourceData, scoreRange, visibleNodeTypes, newVisibleInteractionTypes);
    }
  };

  // Handle container resize and mouse movement for tooltip
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect();
      setDimensions({ width: rect.width, height: rect.height });
    };

    const handleMouseMove = (event) => {
      const rect = container.getBoundingClientRect();
      setTooltip(prev => ({ ...prev, x: event.clientX - rect.left + 15, y: event.clientY - rect.top - 15 }));
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('resize', updateDimensions);
      if (container) {
          container.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  // Update graph forces
  useEffect(() => {
    if (fgRef.current && graphData.nodes.length > 0) {
      fgRef.current.d3Force('charge').strength(-10);
      fgRef.current.d3Force('center', forceCenter());
      fgRef.current.d3Force('collision', forceCollide().radius(30));
      
      setTimeout(() => fgRef.current?.zoomToFit(400, 50), 500);
    }
  }, [graphData]);

  // Custom node drawing
  const drawNode = (node, ctx) => {
    const nodeSize = 8;
    const colors = {
      variant: PALETTE.variant, gene: PALETTE.gene,
      drug: PALETTE.drug, protein: PALETTE.protein
    };
    const color = colors[node.type] || PALETTE.text;
    ctx.save();
    ctx.globalAlpha = 0.8;
    ctx.shadowColor = color;
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.arc(node.x, node.y, nodeSize, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 12px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.fillText(node.name, node.x, node.y + nodeSize + 15);
    ctx.restore();
  };
  
  // Custom link drawing
  const drawLink = (link, ctx) => {
     const { source, target, weight } = link;
     if (!source || !target) return;
 
     const [min, max] = [0, 1]; // Use fixed 0-1 range for color normalization
     const normalizedWeight = max > min ? (weight - min) / (max - min) : 0;
     const red = Math.floor((1 - normalizedWeight) * 255);
     const green = Math.floor(normalizedWeight * 255);
     const blue = 100;
     
     ctx.save();
     ctx.globalAlpha = 0.6 + (normalizedWeight * 0.4);
     ctx.strokeStyle = `rgb(${red}, ${green}, ${blue})`;
     ctx.lineWidth = 1 + (normalizedWeight * 2);
     ctx.beginPath();
     ctx.moveTo(source.x, source.y);
     ctx.lineTo(target.x, target.y);
     ctx.stroke();
     ctx.restore();
  };

  // Handle link hover to show/hide the tooltip
  const handleLinkHover = (link) => {
    if (link) {
      const content = (
        <>
          {`${link.source.name} → ${link.target.name}`}
          <br />
          {`Type: ${link.interactionType}`}
          <br />
          {`Weight: ${link.weight.toFixed(3)}`}
        </>
      );
      setTooltip(prev => ({ ...prev, visible: true, content }));
    } else {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  // Calculate interaction counts for the stats panel
  const interactionCounts = graphData.links.reduce((acc, link) => {
    acc[link.interactionType] = (acc[link.interactionType] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="multivariant-network-container">
      <div className="multivariant-network-controls">
        <div className="score-range-control">
          <label>Weight Filter:</label>
          <Slider 
            range 
            min={0} 
            max={1} 
            step={0.01} 
            value={scoreRange} 
            onChange={(newRange) => handleScoreRangeChange(newRange)} 
            className="weight-slider" 
          />
          <span className="range-display">{scoreRange[0].toFixed(2)} - {scoreRange[1].toFixed(2)}</span>
        </div>
        
        {/* Raw Data Breakdown */}
        <div className="data-breakdown">
          <div className="breakdown-section">
            <h4>Raw File Contents</h4>
            <div className="breakdown-stats">
              <div className="stat-group">
                <span className="stat-title">Node Types:</span>
                {Object.entries(
                  (() => {
                    const counts = {};
                    if (data) {
                      const parsedData = parseNetworkData(data);
                      parsedData.nodes.forEach(node => {
                        counts[node.type] = (counts[node.type] || 0) + 1;
                      });
                    }
                    return counts;
                  })()
                ).map(([type, count]) => (
                  <span key={type} className="stat-item">
                    {type.charAt(0).toUpperCase() + type.slice(1)}: {count}
                  </span>
                ))}
              </div>
              <div className="stat-group">
                <span className="stat-title">Interaction Types:</span>
                {Object.entries(
                  (() => {
                    const counts = {};
                    if (data) {
                      const parsedData = parseNetworkData(data);
                      parsedData.links.forEach(link => {
                        counts[link.interactionType] = (counts[link.interactionType] || 0) + 1;
                      });
                    }
                    return counts;
                  })()
                ).map(([type, count]) => (
                  <span key={type} className="stat-item">
                    {type}: {count}
                  </span>
                ))}
              </div>
              <div className="stat-group">
                <span className="stat-title">Totals:</span>
                <span className="stat-item">
                  Nodes: {(() => {
                    if (data) {
                      const parsedData = parseNetworkData(data);
                      return parsedData.nodes.length;
                    }
                    return 0;
                  })()}
                </span>
                <span className="stat-item">
                  Edges: {(() => {
                    if (data) {
                      const parsedData = parseNetworkData(data);
                      return parsedData.links.length;
                    }
                    return 0;
                  })()}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="filter-controls">
          <div className="filter-group">
            <label>Node Types:</label>
            <div className="checkbox-group">
              {Object.entries(visibleNodeTypes).map(([type, visible]) => (
                <label key={type} className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={visible} 
                    onChange={() => handleNodeTypeChange(type)}
                  />
                  <span className="checkbox-label">{type.charAt(0).toUpperCase() + type.slice(1)}s</span>
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Interactions:</label>
            <div className="checkbox-group">
              {interactionTypes.map(type => (
                <label key={type} className="checkbox-item">
                  <input 
                    type="checkbox" 
                    checked={visibleInteractionTypes[type]} 
                    onChange={() => handleInteractionTypeChange(type)}
                  />
                  <span className="checkbox-label">{type}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        
        <div className="network-metrics">
          <div className="metric-item">
            <span className="metric-value">{graphData.nodes.length}</span>
            <span className="metric-label">Filtered Nodes</span>
          </div>
          <div className="metric-item">
            <span className="metric-value">{graphData.links.length}</span>
            <span className="metric-label">Filtered Edges</span>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="multivariant-network-graph">
        {placeholder ? (
          <div className="network-placeholder">{placeholder}</div>
        ) : (
          <>
            {tooltip.visible && (
              <div
                className="network-tooltip"
                style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
              >
                {tooltip.content}
              </div>
            )}
            <ForceGraph2D
              ref={fgRef}
              width={dimensions.width}
              height={dimensions.height}
              graphData={graphData}
              nodeLabel={node => `${node.type}: ${node.name}`}
              nodeCanvasObject={drawNode}
              linkCanvasObject={drawLink}
              linkHoverPrecision={8}
              onLinkHover={handleLinkHover}
              cooldownTicks={200}
              d3AlphaDecay={0.02}
              d3VelocityDecay={0.3}
              enableZoomInteraction={true}
              enablePanInteraction={true}
              enableNodeDrag={true}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MultiVariantNetwork;